/**
 * AdminGrantModal — System admin modal for granting impact credit.
 *
 * Allows a system admin to award impact credit (any dimension, any magnitude)
 * to any user on the platform. Creates an admin_grant impact event.
 *
 * Triggered from admin console or user profile page.
 */

import React, { useState, useCallback, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CloseButton from "../CloseButton/CloseButton";
import { adminGrantImpactCredit } from "../../api/impactSignature";
import { fetchAllSecurityUsers, SecurityUserResponse } from "../../api/security";
import styles from "./AdminGrantModal.module.css";

const DIMENSIONS = ["mind", "body", "heart", "wings", "roots", "voice"] as const;
type Dimension = (typeof DIMENSIONS)[number];

interface Props {
  /** Pre-selected user GUID (optional, e.g. when opened from a user profile). */
  preselectedUserGuid?: string;
  onClose: () => void;
  onGranted?: () => void;
}

const AdminGrantModal: React.FC<Props> = ({
  preselectedUserGuid,
  onClose,
  onGranted,
}) => {
  const { t } = useAppTranslation("projectTeam");

  // ── User search state ──
  const [allUsers, setAllUsers] = useState<SecurityUserResponse[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<SecurityUserResponse | null>(null);

  useEffect(() => {
    fetchAllSecurityUsers()
      .then((users) => {
        const active = users.filter((u) => u.isActive);
        setAllUsers(active);
        if (preselectedUserGuid) {
          const pre = active.find(
            (u) => u.userGuid.toUpperCase() === preselectedUserGuid.toUpperCase()
          );
          if (pre) setSelectedUser(pre);
        }
      })
      .catch(() => {});
  }, [preselectedUserGuid]);

  const searchResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (term.length < 2) return [];
    return allUsers
      .filter(
        (u) =>
          u.displayName?.toLowerCase().includes(term) ||
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term)
      )
      .slice(0, 6);
  }, [searchTerm, allUsers]);

  // ── Form state ──
  const [dimension, setDimension] = useState<Dimension>("mind");
  const [magnitude, setMagnitude] = useState<string>("5");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = selectedUser && reason.trim().length > 0 && parseFloat(magnitude) > 0;

  const handleSubmit = useCallback(async () => {
    if (!selectedUser || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await adminGrantImpactCredit({
        userGuid: selectedUser.userGuid,
        dimension,
        magnitude: parseFloat(magnitude),
        reason: reason.trim(),
      });
      setSuccess(true);
      onGranted?.();
    } catch (err: any) {
      setError(err?.message || "Grant failed");
    } finally {
      setSubmitting(false);
    }
  }, [selectedUser, dimension, magnitude, reason, canSubmit, onGranted]);

  return ReactDOM.createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.title}>{t("adminGrant.title")}</span>
          <CloseButton onClick={onClose} size="md" />
        </div>

        <div className={styles.body}>
          {success ? (
            <div className={styles.successMessage}>
              <div className={styles.successIcon}>✓</div>
              <div>{t("adminGrant.success")}</div>
              <button type="button" className={styles.doneBtn} onClick={onClose}>
                {t("adminGrant.done")}
              </button>
            </div>
          ) : (
            <>
              {/* User picker */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>{t("adminGrant.recipient")}</label>
                {selectedUser ? (
                  <div className={styles.selectedUser}>
                    <span>{selectedUser.displayName || selectedUser.name}</span>
                    <button
                      type="button"
                      className={styles.clearBtn}
                      onClick={() => {
                        setSelectedUser(null);
                        setSearchTerm("");
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      className={styles.input}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t("adminGrant.searchUser")}
                      autoFocus
                    />
                    {searchResults.length > 0 && (
                      <div className={styles.dropdown}>
                        {searchResults.map((u) => (
                          <div
                            key={u.userGuid}
                            className={styles.dropdownItem}
                            onClick={() => {
                              setSelectedUser(u);
                              setSearchTerm("");
                            }}
                          >
                            <span className={styles.dropdownName}>
                              {u.displayName || u.name}
                            </span>
                            {u.email && (
                              <span className={styles.dropdownEmail}>{u.email}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Dimension */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>{t("adminGrant.dimension")}</label>
                <div className={styles.dimGrid}>
                  {DIMENSIONS.map((dim) => (
                    <button
                      key={dim}
                      type="button"
                      className={`${styles.dimBtn} ${dimension === dim ? styles.dimBtnActive : ""}`}
                      onClick={() => setDimension(dim)}
                    >
                      {t(`dimensions.${dim}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Magnitude */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>{t("adminGrant.magnitude")}</label>
                <input
                  type="number"
                  className={styles.input}
                  value={magnitude}
                  onChange={(e) => setMagnitude(e.target.value)}
                  min="0.1"
                  max="100"
                  step="0.5"
                />
                <div className={styles.fieldHint}>{t("adminGrant.magnitudeHint")}</div>
              </div>

              {/* Reason */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>{t("adminGrant.reason")}</label>
                <textarea
                  className={styles.textarea}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("adminGrant.reasonPlaceholder")}
                  rows={3}
                />
              </div>

              {error && <div className={styles.error}>{error}</div>}

              {/* Submit */}
              <button
                type="button"
                className={styles.submitBtn}
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
              >
                {submitting ? t("adminGrant.granting") : t("adminGrant.grant")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AdminGrantModal;
